/**
 * Horizontal GCU stepper (design-spec.md §3 — 9-step flow, cr2a aesthetic).
 * Renders from a GcuProcessState the parent fetches via GET /companies/:id/gcu.
 * Step status is derived from the process's currentStep / stepNumber.
 *
 * To keep the bar legible we collapse the 9 canonical steps into 6 milestone
 * groups (matching the POC stepper); the active group is whichever contains the
 * server's currentStep. The active pill uses spring-soft motion on change.
 */
import { motion, useReducedMotion } from 'framer-motion';
import { Check } from 'lucide-react';
import { GCU_STEPS, gcuStepNumber, type GcuStep, type GcuProcessState } from '@klinik/shared';
import { SPRING_SOFT } from '../lib/motion';

interface Milestone {
  label: string;
  /** 1-based GCU step numbers this milestone covers. */
  steps: number[];
}

// Group the 9 steps into 6 display milestones (intake.md §D order preserved).
const MILESTONES: Milestone[] = [
  { label: 'Identitas & PIC', steps: [1] },
  { label: 'Upload Data', steps: [2] },
  { label: 'Kuesioner PIC', steps: [3, 4, 5] },
  { label: 'Verifikasi Owner', steps: [6, 7] },
  { label: 'Laporan GCU', steps: [8] },
  { label: 'Selesai', steps: [9] },
];

function milestoneState(m: Milestone, currentStepNumber: number): 'done' | 'active' | 'todo' {
  const max = Math.max(...m.steps);
  const min = Math.min(...m.steps);
  if (currentStepNumber > max) return 'done';
  if (currentStepNumber >= min) return 'active';
  return 'todo';
}

export function GcuStepper({ gcu }: { gcu: GcuProcessState | null }) {
  const reduce = useReducedMotion();
  // Fall back to step 2 (upload) for the intake context when state is absent.
  const currentStep: GcuStep = gcu?.currentStep ?? 'upload_documents';
  const currentStepNumber = gcu?.stepNumber ?? gcuStepNumber(currentStep);
  const totalSteps = gcu?.totalSteps ?? GCU_STEPS.length;

  return (
    <div
      className="flex items-center gap-1 overflow-x-auto pb-1"
      role="list"
      aria-label={`Progres General Check-Up, langkah ${currentStepNumber} dari ${totalSteps}`}
    >
      {MILESTONES.map((m, i) => {
        const st = milestoneState(m, currentStepNumber);
        const done = st === 'done';
        const active = st === 'active';
        const connectorOn = currentStepNumber >= Math.min(...m.steps);
        return (
          <div key={m.label} className="flex shrink-0 items-center gap-1" role="listitem">
            {i > 0 && (
              <div className={`h-0.5 w-6 ${connectorOn ? 'bg-brand' : 'bg-slate-200'}`} aria-hidden />
            )}
            <motion.div
              layout={!reduce}
              transition={SPRING_SOFT}
              aria-current={active ? 'step' : undefined}
              className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${
                active
                  ? 'bg-brand text-white'
                  : done
                    ? 'bg-green-100 text-green-700'
                    : 'bg-slate-100 text-slate-400'
              }`}
            >
              <span
                className={`grid h-5 w-5 place-items-center rounded-full ${
                  active
                    ? 'bg-white/20 text-white'
                    : done
                      ? 'bg-green-600 text-white'
                      : 'bg-white text-slate-400'
                }`}
              >
                {done ? <Check size={12} aria-hidden /> : i + 1}
              </span>
              {m.label}
            </motion.div>
          </div>
        );
      })}
    </div>
  );
}
